// Carga de una lista de la nomina (vehiculos o conductores) de la planta
// seleccionada. Mismo patron que sites/useSites.ts: una respuesta vieja (otra
// planta, otra sesion) se descarta, y el error no entra al estado: solo su
// tipo, porque el cuerpo remoto puede traer datos de personas.
import { useCallback, useEffect, useRef, useState } from "react";
import type { PortalSession } from "../auth/session.js";
import { FleetError, type FleetSite, type Roster } from "./port.js";

export type RosterState<T> =
  | { estado: "cargando" }
  | { estado: "denegado" }
  | { estado: "error" }
  | { estado: "listo"; today: string; items: readonly T[] };

export interface RosterHandle<T> {
  state: RosterState<T>;
  reload: () => void;
  /**
   * Relee sin pasar por "cargando": la tabla queda en pantalla y se reemplaza
   * al llegar la respuesta. Lo usa la carga de documentos (PP-23) para que el
   * badge de elegibilidad refleje lo que calculo la API sin perder la ficha.
   */
  refresh: () => void;
  /** Reemplaza el listado ya cargado con lo que devolvio una escritura. */
  update: (change: (items: readonly T[]) => readonly T[]) => void;
}

export function useRoster<T>(
  load: (session: PortalSession, site: FleetSite) => Promise<Roster<T>>,
  session: PortalSession,
  site: FleetSite,
): RosterHandle<T> {
  const [state, setState] = useState<RosterState<T>>({ estado: "cargando" });
  const [round, setRound] = useState(0);
  const silentNext = useRef(false);
  const { siteId, providerId } = site;

  useEffect(() => {
    let current = true;
    // Solo la relectura pedida por refresh es silenciosa; un cambio de planta
    // o de sesion siempre vuelve a "cargando".
    const silent = silentNext.current;
    silentNext.current = false;
    if (!silent) setState({ estado: "cargando" });
    load(session, { siteId, providerId })
      .then((roster) => {
        if (current) setState({ estado: "listo", today: roster.today, items: roster.items });
      })
      .catch((error: unknown) => {
        if (current) setState({ estado: error instanceof FleetError && error.kind === "access" ? "denegado" : "error" });
      });
    return () => {
      current = false;
    };
  }, [load, session, siteId, providerId, round]);

  const reload = useCallback(() => {
    setRound((n) => n + 1);
  }, []);

  const refresh = useCallback(() => {
    silentNext.current = true;
    setRound((n) => n + 1);
  }, []);

  const update = useCallback((change: (items: readonly T[]) => readonly T[]) => {
    setState((previous) => (previous.estado === "listo" ? { ...previous, items: change(previous.items) } : previous));
  }, []);

  return { state, reload, refresh, update };
}
